import type { Zone } from '../../design/entities/zone'
import type { CanvasElement } from '../entities/element'

/**
 * Element Position Service
 * Domain service that handles element positioning logic
 */

export type Position = { x: number; y: number }

export const createElementPositionService = () => ({
  /**
   * Constrains element position within design zone boundaries
   * Ensures elements never completely disappear from the viewport
   * Works generically for all element types (text, image, asset)
   * Elements are positioned by their center point
   */
  constrainToDesignZone: (
    position: Position,
    element: Pick<CanvasElement, 'type' | 'dimensions'>,
    designZone: Zone,
  ): Position => {
    // For elements with dimensions, we need to account for their size
    // Elements are positioned by their center, so we need to ensure
    // at least part of the element remains visible
    const elementWidth = element.dimensions?.width || 0
    const elementHeight = element.dimensions?.height || 0

    // We allow elements to be partially outside the zone
    // but ensure at least 20% of the element remains visible
    const minVisibleWidth = Math.max(elementWidth * 0.2, 10) // At least 10mm visible
    const minVisibleHeight = Math.max(elementHeight * 0.2, 10) // At least 10mm visible

    // Calculate how far the center can move while keeping minimum visibility
    // Since elements are centered, we need to calculate based on half dimensions
    const halfWidth = elementWidth / 2
    const halfHeight = elementHeight / 2

    // The center can move outside the zone by (halfWidth - minVisibleWidth)
    // This ensures at least minVisibleWidth remains inside
    const leftOverhang = halfWidth - minVisibleWidth
    const topOverhang = halfHeight - minVisibleHeight

    // Minimum center position (can go left/up beyond the zone edge)
    const minX = designZone.left - leftOverhang
    const minY = designZone.top - topOverhang

    // Maximum center position (can go right/down beyond the zone edge)
    const maxX = designZone.left + designZone.width + leftOverhang
    const maxY = designZone.top + designZone.height + topOverhang

    // For very large elements, ensure they can at least be centered in the zone
    const centerX = designZone.left + designZone.width / 2
    const centerY = designZone.top + designZone.height / 2

    // Use the most permissive bounds
    const validMinX = Math.min(minX, centerX)
    const validMaxX = Math.max(maxX, centerX)
    const validMinY = Math.min(minY, centerY)
    const validMaxY = Math.max(maxY, centerY)

    // Constrain position to calculated boundaries
    const constrainedX = Math.max(validMinX, Math.min(validMaxX, position.x))
    const constrainedY = Math.max(validMinY, Math.min(validMaxY, position.y))

    return { x: constrainedX, y: constrainedY }
  },

  /**
   * Calculates the center position of a design zone
   */
  calculateCenterPosition: (zone: Zone): Position => {
    return {
      x: zone.left + zone.width / 2,
      y: zone.top + zone.height / 2,
    }
  },

  /**
   * Calculates drag delta with scale compensation
   */
  calculateDragDelta: (startPos: Position, currentPos: Position, scale: number): Position => {
    return {
      x: (currentPos.x - startPos.x) / scale,
      y: (currentPos.y - startPos.y) / scale,
    }
  },

  /**
   * Validates if a position is within the design zone
   */
  isPositionValid: (position: Position, designZone: Zone): boolean => {
    return (
      position.x >= designZone.left &&
      position.x <= designZone.left + designZone.width &&
      position.y >= designZone.top &&
      position.y <= designZone.top + designZone.height
    )
  },

  /**
   * Calculates optimal position for new elements based on existing elements
   */
  calculateOptimalPosition: (
    existingElements: Array<{ position: Position }>,
    designZone: Zone,
    preferCenter: boolean = true,
  ): Position => {
    // If no elements exist or prefer center, return center position
    if (existingElements.length === 0 || preferCenter) {
      return {
        x: designZone.left + designZone.width / 2,
        y: designZone.top + designZone.height / 2,
      }
    }

    // Otherwise, find a position that doesn't overlap too much
    // Simple algorithm: offset from last element
    const lastElement = existingElements[existingElements.length - 1]
    const offset = 20 // mm

    const newPosition = {
      x: lastElement.position.x + offset,
      y: lastElement.position.y + offset,
    }

    // Wrap around if we go outside the zone
    if (newPosition.x > designZone.left + designZone.width - 50) {
      newPosition.x = designZone.left + 50
    }
    if (newPosition.y > designZone.top + designZone.height - 50) {
      newPosition.y = designZone.top + 50
    }

    return newPosition
  },

  /**
   * Calculates position for centering an element within a zone
   */
  centerElementInZone: (
    elementDimensions: { width: number; height: number },
    zone: Zone,
  ): Position => {
    return {
      x: zone.left + (zone.width - elementDimensions.width) / 2,
      y: zone.top + (zone.height - elementDimensions.height) / 2,
    }
  },
})
